"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import type {
  AttachFileRecord,
  AttachGroupRecord,
  AttachGroupInput,
  AttachListResponse,
  AttachPageSize,
  AttachPickerInitResponse,
  AttachPickerModalProps,
  AttachQuotaRecord,
  AttachUploadRulesRecord,
  AttachViewMode,
} from "@/components/admin/attach-picker/types";
import {
  attachApi,
  canSelectAttach,
  formatFileSize,
  formatQuotaText,
  getCachedUploadRules,
  setCachedUploadRules,
} from "@/components/admin/attach-picker/utils";
import { toast } from "sonner";
import { ADMIN_DIALOG_CLOSE_DELAY_MS } from "@/hooks/use-admin-dialog";
import { ApiError, type ApiInit } from "@/lib/api-client";
import { deriveUploadKind, isUploadFileAllowed, type UploadKind } from "@/lib/upload/kinds";
import {
  createAttachUploadTask,
  revokeAttachUploadTaskPreview,
  uploadAttachWithProgress,
  type AttachUploadTask,
} from "@/lib/upload/attach-upload-client";
import type { AttachUploadContext, AttachVisibility } from "@/services/attach-service";
import { createRemoteAttachTask, mergeRemoteAttachUrls, parseRemoteAttachText, remoteAttachUrlKey, uploadRemoteAttachWithProgress, REMOTE_ATTACH_MAX_ROWS, type RemoteAttachTask, type RemotePasteResult } from "@/lib/upload/remote-attach-client";
import { queueAttachUpload } from "@/lib/upload/attach-upload-queue";

type UploadRules = AttachUploadRulesRecord;
type AttachTexts = ReturnType<typeof useTranslations<"site.attach">>;

const DAY_MS = 24 * 60 * 60 * 1000;

function buildQuotaNotice(quota: AttachQuotaRecord, t: AttachTexts) {
  if (quota.overQuota) {
    return {
      tone: "danger" as const,
      text: t("quotaOver", { size: formatFileSize(quota.overSizeBytes) }),
    };
  }

  if (quota.expiringSoon && quota.packExpiresAt) {
    const days = Math.max(
      0,
      Math.ceil((new Date(quota.packExpiresAt).getTime() - Date.now()) / DAY_MS),
    );

    return {
      tone: "warning" as const,
      text: t("quotaExpiring", { days }),
    };
  }

  return null;
}

type FileDeleteTarget =
  | { mode: "single"; id: string }
  | { mode: "batch" };

type UseAttachPickerControllerParams = Pick<
  AttachPickerModalProps,
  | "open"
  | "onOpenChange"
  | "kind"
  | "module"
  | "acceptExtensions"
  | "allowedKinds"
  | "onSelect"
  | "multiple"
  | "maxCount"
  | "onSelectMany"
>;

export function useAttachPickerController({
  open,
  onOpenChange,
  kind,
  module,
  acceptExtensions,
  allowedKinds,
  onSelect,
  multiple = false,
  maxCount,
  onSelectMany,
}: UseAttachPickerControllerParams) {
  const t = useTranslations("site.attach");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const requestFailed = t("requestFailed");
  const api = useCallback(
    <T>(url: string, init?: ApiInit) => attachApi<T>(url, init, requestFailed),
    [requestFailed],
  );

  const isMulti = multiple === true;

  const effectiveMaxCount = isMulti
    ? maxCount && maxCount > 0
      ? maxCount
      : 100
    : 1;

  const defaultTypeFilter: UploadKind | "all" = kind === "file" ? "all" : kind;

  const [tab, setTab] = useState<"local" | "remote">("local");
  const [typeFilter, setTypeFilter] = useState<UploadKind | "all">(
    defaultTypeFilter,
  );
  const [groups, setGroups] = useState<AttachGroupRecord[]>([]);
  const [activeGroupId, setActiveGroupId] = useState("default");
  const [items, setItems] = useState<AttachFileRecord[]>([]);
  const selectedFilesRef = useRef(new Map<string, AttachFileRecord>());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<AttachPageSize>(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [quotaText, setQuotaText] = useState("");
  const [quotaNotice, setQuotaNotice] = useState<{
    tone: "warning" | "danger";
    text: string;
  } | null>(null);
  const firstRemoteId = useId();
  const [remoteTasks, setRemoteTasks] = useState<RemoteAttachTask[]>(() => [createRemoteAttachTask("", firstRemoteId)]);
  const remoteTasksRef = useRef(remoteTasks);
  const [rules, setRules] = useState<UploadRules | null>(null);
  const [uploadContext, setUploadContext] = useState<AttachUploadContext | null>(
    null,
  );
  const [uploadVisibility, setUploadVisibility] =
    useState<AttachVisibility>("private");
  const [initialLoading, setInitialLoading] = useState(false);
  const [listFetching, setListFetching] = useState(false);
  const itemsRef = useRef<AttachFileRecord[]>([]);
  const listRequestRef = useRef(0);

  const listPendingRef = useRef(0);
  const initialPendingRef = useRef(0);
  const bootstrapRequestRef = useRef(0);
  const bootstrappedRef = useRef(false);
  const uploadAbortRef = useRef<Map<string, AbortController>>(new Map());
  const [uploadTasks, setUploadTasks] = useState<AttachUploadTask[]>([]);
  const uploadTasksRef = useRef<AttachUploadTask[]>([]);
  useEffect(() => { uploadTasksRef.current = uploadTasks; }, [uploadTasks]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const remoteAbortRef = useRef<AbortController | null>(null);
  const remoteOpenRef = useRef(open);
  const remoteCurrentRef = useRef<{ id: string; controller: AbortController } | null>(null);
  const [remoteQueueProgress, setRemoteQueueProgress] = useState({ completed: 0, total: 0 });

  const updateRemoteTasks = useCallback((update: (tasks: RemoteAttachTask[]) => RemoteAttachTask[]) => {
    const next = update(remoteTasksRef.current);
    remoteTasksRef.current = next;
    setRemoteTasks(next);
  }, []);
  const handleRemoteRowsChange = useCallback((rows: Array<{ id: string; url: string }>) => {
    if (rows.length > REMOTE_ATTACH_MAX_ROWS) {
      toast.error(t("remoteRowLimit", { max: REMOTE_ATTACH_MAX_ROWS }));
      return;
    }
    const current = remoteTasksRef.current;
    const byId = new Map(current.map(task => [task.id, task]));
    const active = remoteCurrentRef.current;
    if (active && !rows.some(row => row.id === active.id)) active.controller.abort();

    const retainedIds = new Set(rows.map(row => row.id));
    const nextRows = remoteAbortRef.current ? current.filter(task => retainedIds.has(task.id)) : rows;
    updateRemoteTasks(() => nextRows.map(row => {
      const previous = byId.get(row.id);
      if (previous?.url === row.url) return previous;
      // 已提交的地址保持只读；删除任务不触碰已入库附件。
      if (previous && (remoteAbortRef.current || previous.status === "success")) return previous;
      return createRemoteAttachTask(row.url, row.id);
    }));
  }, [updateRemoteTasks, t]);
  const [selectionVisible, setSelectionVisible] = useState(false);
  const [viewMode, setViewMode] = useState<AttachViewMode>("grid");
  const [moveFile, setMoveFile] = useState<AttachFileRecord | null>(null);
  const [moveSaving, setMoveSaving] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetGroupId, setMoveTargetGroupId] = useState("");
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<string | null>(
    null,
  );
  const [fileDeleteTarget, setFileDeleteTarget] =
    useState<FileDeleteTarget | null>(null);
  const [fileDeleteSaving, setFileDeleteSaving] = useState(false);

  const effectiveExtensions = useMemo(() => {
    const extensions = acceptExtensions ?? rules?.extensions ?? [];
    return allowedKinds ? extensions.filter(extension => allowedKinds.some(kind => kind === deriveUploadKind(extension))) : extensions;
  }, [acceptExtensions, allowedKinds, rules?.extensions]);
  const uploading = uploadTasks.some((task) => task.status === "uploading");

  const updateUploadTask = useCallback(
    (taskId: string, patch: Partial<AttachUploadTask>) => {
      setUploadTasks((current) =>
        current.map((task) =>
          task.id === taskId ? { ...task, ...patch } : task,
        ),
      );
    },
    [],
  );

  const removeUploadTask = useCallback((taskId: string) => {
    setUploadTasks((current) => {
      const target = current.find((task) => task.id === taskId);

      if (target) {
        revokeAttachUploadTaskPreview(target);
      }

      return current.filter((task) => task.id !== taskId);
    });
    uploadAbortRef.current.delete(taskId);
  }, []);

  useEffect(() => () => {
    remoteOpenRef.current = false;
    remoteAbortRef.current?.abort();
    uploadAbortRef.current.forEach((controller) => controller.abort());
    uploadAbortRef.current.clear();
    uploadTasksRef.current.forEach(revokeAttachUploadTaskPreview);
  }, []);

  useEffect(() => {
    remoteOpenRef.current = open;
    if (!open) remoteAbortRef.current?.abort();
  }, [open]);

  const handleCancelUpload = useCallback(
    (taskId: string) => {
      uploadAbortRef.current.get(taskId)?.abort("discard");
      removeUploadTask(taskId);
    },
    [removeUploadTask],
  );

  const pickerTexts = useMemo(
    () => ({
      title: t("upload", { kind }),
      uploadButton: t("upload", { kind }),
      localTab: t("localTab", { kind }),
      remoteTab: t("remoteTab", { kind }),
      sourceAriaLabel: t("source", { kind }),
      emptyHint: t("empty", { kind }),
      remotePlaceholder: t("urlPlaceholder", { kind }),
      remoteExtract: t("extract", { kind }),
      remoteUrlRequired: t("urlPlaceholder", { kind }),
    }),
    [t, kind],
  );

  const attachTabItems = useMemo(
    () =>
      [
        { value: "local" as const, label: pickerTexts.localTab },
        { value: "remote" as const, label: pickerTexts.remoteTab },
      ] as const,
    [pickerTexts.localTab, pickerTexts.remoteTab],
  );

  const pickedFile = useMemo(
    () => items.find((item) => item.id === pickedId) ?? null,
    [items, pickedId],
  );

  useEffect(() => { itemsRef.current = items; }, [items]);

  const applyUploadRules = useCallback((nextRules: UploadRules) => {
    setRules(nextRules);
    setUploadContext(
      (nextRules.uploadContext as AttachUploadContext | undefined) ?? null,
    );
    setUploadVisibility(
      (nextRules.uploadContext?.defaultVisibility as AttachVisibility) ??
        "private",
    );
    setCachedUploadRules(kind, module, nextRules);
  }, [kind, module]);

  const applyListPayload = useCallback(
    (data: AttachListResponse) => {
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setQuotaText(
        formatQuotaText(
          data.quota.usedSizeBytes,
          data.quota.maxSizeBytes,
        ),
      );
      setQuotaNotice(buildQuotaNotice(data.quota, t));
      const nextIds = new Set(data.items.map((file) => file.id));
      setSelectedIds((selected) => {
        const keep = new Set(selected);
        for (const id of selectedFilesRef.current.keys()) {
          if (!keep.has(id) && !nextIds.has(id)) selectedFilesRef.current.delete(id);
        }
        return selected;
      });
      for (const file of data.items) selectedFilesRef.current.set(file.id, file);
    },
    [t],
  );

  const loadGroups = useCallback(async () => {
    const result = await api<{ groups: AttachGroupRecord[] }>(
      `/api/attach/groups`,
    );

    if (!result.ok) {
      toast.error(result.message);
      return false;
    }

    setGroups(result.data.groups);

    if (
      result.data.groups.length > 0 &&
      !result.data.groups.some((group) => group.id === activeGroupId)
    ) {
      setActiveGroupId(result.data.groups[0]!.id);
    }

    return true;
  }, [activeGroupId, api]);

  const loadList = useCallback(async () => {
    const requestId = ++listRequestRef.current;
    const isInitial = itemsRef.current.length === 0;
    const setBusy = isInitial ? setInitialLoading : setListFetching;
    const pendingRef = isInitial ? initialPendingRef : listPendingRef;

    pendingRef.current += 1;
    setBusy(true);

    try {
      const params = new URLSearchParams({
        groupId: activeGroupId,
        page: String(page),
        pageSize: String(pageSize),
      });

      if (typeFilter !== "all") {
        params.set("kind", typeFilter);
      }

      const result = await api<AttachListResponse>(
        `/api/attach/list?${params.toString()}`,
      );

      if (requestId !== listRequestRef.current) {
        return false;
      }

      if (!result.ok) {
        toast.error(result.message);
        return false;
      }

      applyListPayload(result.data);

      return true;
    } finally {
      pendingRef.current -= 1;

      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        setBusy(false);
      }
    }
  }, [activeGroupId, api, applyListPayload, page, pageSize, typeFilter]);

  const refreshAll = useCallback(async () => {
    await loadGroups();
    await loadList();
  }, [loadGroups, loadList]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const cachedRules = getCachedUploadRules(kind, module);

    if (cachedRules) {
      applyUploadRules(cachedRules);
    }

    const requestId = ++bootstrapRequestRef.current;
    const snapshot = {
      groupId: activeGroupId,
      page,
      pageSize,
    };

    setInitialLoading(true);

    void (async () => {
      const params = new URLSearchParams({
        kind,
        groupId: snapshot.groupId,
        page: String(snapshot.page),
        pageSize: String(snapshot.pageSize),
      });

      if (defaultTypeFilter !== "all") {
        params.set("filterKind", defaultTypeFilter);
      }

      if (module) {
        params.set("module", module);
      }

      const result = await api<AttachPickerInitResponse>(
        `/api/attach/picker/init?${params.toString()}`,
      );

      if (requestId !== bootstrapRequestRef.current) {
        return;
      }

      setInitialLoading(false);

      if (!result.ok) {
        toast.error(result.message);
        return;
      }

      applyUploadRules(result.data.rules);
      setGroups(result.data.groups);

      const resolvedGroupId =
        result.data.groups.length > 0 &&
        !result.data.groups.some((group) => group.id === snapshot.groupId)
          ? result.data.groups[0]!.id
          : snapshot.groupId;

      bootstrappedRef.current = true;

      if (resolvedGroupId !== snapshot.groupId) {
        setActiveGroupId(resolvedGroupId);
        return;
      }

      applyListPayload({
        items: result.data.items,
        total: result.data.total,
        page: result.data.page,
        pageSize: result.data.pageSize,
        totalPages: result.data.totalPages,
        quota: result.data.quota,
      });
    })();

    return () => {
      bootstrapRequestRef.current += 1;
    };
  }, [api, applyListPayload, applyUploadRules, kind, module, open]);

  useEffect(() => {
    if (!open || !bootstrappedRef.current) {
      return;
    }

    void loadList();

    return () => {
      listRequestRef.current += 1;
    };
  }, [activeGroupId, loadList, page, pageSize]);

  useEffect(() => {
    if (open) {

      setTypeFilter(defaultTypeFilter);
    }
  }, [open, defaultTypeFilter]);

  useEffect(() => {

    if (open && isMulti) {
      setSelectionVisible(true);
    }
  }, [open, isMulti]);

  useEffect(() => {
    if (open) {
      return;
    }

    bootstrapRequestRef.current += 1;
    listRequestRef.current += 1;
    bootstrappedRef.current = false;
    uploadAbortRef.current.forEach((controller) => controller.abort());
    uploadAbortRef.current.clear();

    const timer = window.setTimeout(() => {
      setUploadTasks((current) => {
        current.forEach((task) => revokeAttachUploadTaskPreview(task));
        return [];
      });
      setTab("local");
      setSelectedIds([]);
      selectedFilesRef.current.clear();
      setSelectionVisible(false);
      setViewMode("grid");
      setPickedId(null);
      updateRemoteTasks(() => [createRemoteAttachTask()]);
      setRemoteQueueProgress({ completed: 0, total: 0 });
      setPage(1);
      setTypeFilter(defaultTypeFilter);
      setMoveOpen(false);
      setMoveFile(null);
      setDeleteGroupTarget(null);
      setFileDeleteTarget(null);
      setUploadVisibility("private");
      setUploadContext(null);
    }, ADMIN_DIALOG_CLOSE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [open, defaultTypeFilter, updateRemoteTasks]);

  const handleToggleSelect = useCallback((id: string) => {
    const file = selectedFilesRef.current.get(id);
    if (isMulti && file && !canSelectAttach(file, effectiveExtensions, allowedKinds).ok) {
      toast.error(t("typeMismatch"));
      return;
    }
    setSelectedIds((current) => {
      const isSelected = current.includes(id);

      if (isSelected) {
        const next = current.filter((item) => item !== id);

        if (!isMulti && next.length === 0) {
          setSelectionVisible(false);
        }
        return next;
      }

      if (isMulti && current.length >= effectiveMaxCount) {
        toast.error(t("maxSelect", { count: effectiveMaxCount }));
        return current;
      }

      setSelectionVisible(true);
      return [...current, id];
    });
  }, [allowedKinds, effectiveExtensions, effectiveMaxCount, isMulti, t]);

  const handlePick = useCallback((file: AttachFileRecord) => {
    const check = canSelectAttach(file, effectiveExtensions, allowedKinds);

    if (!check.ok) {
      toast.error(t("typeMismatch"), {
        description: t("onlySupports", {
          extensions: check.extensions.join(t("listSeparator")),
        }),
      });
      return;
    }

    if (isMulti) {
      handleToggleSelect(file.id);
      return;
    }

    setPickedId(file.id);
    onSelect(file);
    onOpenChange(false);
  }, [allowedKinds, effectiveExtensions, handleToggleSelect, isMulti, onOpenChange, onSelect, t]);

  function handleConfirm() {
    if (isMulti) {
      if (selectedIds.length === 0) {
        onOpenChange(false);
        return;
      }

      const selectedFiles = selectedIds
        .map((id) => selectedFilesRef.current.get(id))
        .filter((file): file is AttachFileRecord => Boolean(file));

      onSelectMany?.(selectedFiles);
      onOpenChange(false);
      return;
    }

    if (!pickedFile) {
      onOpenChange(false);
      return;
    }

    handlePick(pickedFile);
  }

  const activeGroup = groups.find((group) => group.id === activeGroupId) ?? null;
  const activeGroupWritable = activeGroup ? activeGroup.writable : true;
  const readOnlyGroupHint =
    activeGroup?.scope === "org" ? t("orgReadOnly") : t("divisionReadOnly");

  async function handleUploadFiles(files: File[]) {
    if (remoteAbortRef.current) return;
    if (!rules || files.length === 0) {
      return;
    }

    if (!activeGroupWritable) {
      toast.error(readOnlyGroupHint);
      return;
    }

    for (const task of uploadTasksRef.current) {
      if (task.status === "error" && files.some((file) => file.name === task.file.name && file.size === task.file.size && file.lastModified === task.file.lastModified)) removeUploadTask(task.id);
    }

    const uploadRules = {
      extensions: allowedKinds || effectiveExtensions.length ? effectiveExtensions : rules.extensions,
      maxSizeMb: rules.maxSizeMb,
    };

    const queuedTasks: AttachUploadTask[] = [];
    const validTasks: AttachUploadTask[] = [];
    const validationFailures: string[] = [];
    const uploadFailureMessages: string[] = [];

    for (const file of files) {
      const task = createAttachUploadTask(file);
      const check = isUploadFileAllowed(file.name, file.size, uploadRules);

      if (!check.ok) {
        const message =
          check.reason === "empty"
            ? t("fileRequired")
            : check.reason === "tooLarge"
              ? t("fileTooLarge", { max: uploadRules.maxSizeMb })
              : t("onlySupports", {
                  extensions: uploadRules.extensions.join(t("listSeparator")),
                });

        queuedTasks.push({
          ...task,
          status: "error",
          error: message,
        });
        validationFailures.push(
          files.length > 1 ? t("fileError", { name: file.name, message }) : message,
        );
        continue;
      }

      queuedTasks.push(task);
      validTasks.push(task);
    }

    setUploadTasks((current) => [...queuedTasks, ...current]);

    if (validTasks.length === 0) {
      toast.error(validationFailures[0] ?? t("uploadFailed"), {
        description:
          validationFailures.length > 1
            ? validationFailures.slice(1, 5).join("\n")
            : undefined,
      });
      return;
    }

    for (const task of validTasks) uploadAbortRef.current.set(task.id, new AbortController());

    async function runUploadTask(task: AttachUploadTask) {
      const controller = uploadAbortRef.current.get(task.id);
      if (!controller || controller.signal.aborted) return null;

      try {
        await uploadAttachWithProgress({
          file: task.file,
          kind,
          groupId: activeGroupId,
          module,
          visibility: uploadContext?.visibilitySelectable
            ? uploadVisibility
            : undefined,
          signal: controller.signal,
          onProgress: (progress) => updateUploadTask(task.id, { progress }),
          onPhase: (phase) => updateUploadTask(task.id, { phase }),
          messages: {
            failed: t("uploadFailedRetry"),
            network: t("networkError"),
          },
        });

        removeUploadTask(task.id);
        return true;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          removeUploadTask(task.id);
          return null;
        }

        const message =
          error instanceof Error ? error.message : t("uploadFailedRetry");

        updateUploadTask(task.id, {
          status: "error",
          error: message,
        });
        uploadFailureMessages.push(t("fileError", { name: task.file.name, message }));
        return false;
      } finally {
        uploadAbortRef.current.delete(task.id);
      }
    }

    const results: Array<boolean | null> = [];
    for (const task of validTasks) results.push(await runUploadTask(task));
    const successCount = results.filter(Boolean).length;
    const uploadFailures = results.filter((result) => result === false).length;

    if (successCount > 0) {
      await refreshAll();
    }

    if (successCount > 0 && uploadFailures === 0 && validationFailures.length === 0) {
      toast.success(
        successCount === 1
          ? t("uploadSuccess")
          : t("uploadedCount", { count: successCount }),
      );
      return;
    }

    if (successCount > 0) {
      toast.success(t("uploadedCount", { count: successCount }));
    }

    if (uploadFailures > 0 || validationFailures.length > 0) {
      toast.error(t("partialFailed"), {
        description: [...validationFailures, ...uploadFailureMessages]
          .slice(0, 5)
          .join("\n") || undefined,
      });
    }
  }

  function handleRemotePaste(text: string): RemotePasteResult {
    if (remoteAbortRef.current || uploadAbortRef.current.size > 0) return { ok: false, reason: "busy" };
    if (!activeGroupWritable) return { ok: false, reason: "readonly" };
    const parsed = parseRemoteAttachText(text);
    if (!parsed.ok) return { ok: false, reason: parsed.issue.reason, issue: parsed.issue };
    if (!parsed.urls.length) return { ok: false, reason: "empty" };
    const result = mergeRemoteAttachUrls(remoteTasksRef.current, parsed.urls);
    if (result.added) updateRemoteTasks(() => result.tasks);
    if (result.overflow) toast.warning(t("remotePasteOverflow", { added: result.added, duplicates: result.duplicates, overflow: result.overflow }));
    else toast.success(t("remotePasteAdded", { added: result.added, duplicates: result.duplicates }));
    return { ok: true, added: result.added, duplicates: result.duplicates, overflow: result.overflow, firstIndex: result.firstIndex };
  }

  async function handleRemoteExtract() {
    if (remoteAbortRef.current) return;
    if (remoteTasksRef.current.length > REMOTE_ATTACH_MAX_ROWS) {
      toast.error(t("remoteRowLimit", { max: REMOTE_ATTACH_MAX_ROWS }));
      return;
    }
    if (!remoteTasksRef.current.some(task => task.url.trim())) {
      toast.error(pickerTexts.remoteUrlRequired);
      return;
    }

    if (!activeGroupWritable) {
      toast.error(readOnlyGroupHint);
      return;
    }

    const seen = new Set(remoteTasksRef.current.filter(task => task.status === "success").map(task => remoteAttachUrlKey(task.url)));
    const ids: string[] = [];
    let duplicates = 0;
    updateRemoteTasks(tasks => tasks.map(task => {
      if (!task.url.trim() || task.status === "success") return task;
      const key = remoteAttachUrlKey(task.url);
      if (seen.has(key)) {
        duplicates += 1;
        return { ...task, status: "duplicate", progress: null, error: t("remoteDuplicate") };
      }
      seen.add(key);
      ids.push(task.id);
      return { ...task, status: "queued", progress: null, error: "" };
    }));
    if (!ids.length) {
      if (duplicates) toast.success(t("remoteDuplicatesSkipped", { count: duplicates }));
      return;
    }

    const batch = new AbortController();
    remoteAbortRef.current = batch;
    setRemoteLoading(true);
    setRemoteQueueProgress({ completed: 0, total: ids.length });
    let succeeded = 0, failed = 0;
    const patch = (id: string, values: Partial<RemoteAttachTask>) => updateRemoteTasks(tasks => tasks.map(task => task.id === id ? { ...task, ...values } : task));
    try {
      for (let index = 0; index < ids.length; index += 1) {
        if (batch.signal.aborted) break;
        const id = ids[index];
        const task = remoteTasksRef.current.find(task => task.id === id);
        if (!task) { setRemoteQueueProgress({ completed: index + 1, total: ids.length }); continue; }
        const controller = new AbortController();
        remoteCurrentRef.current = { id, controller };
        const signal = AbortSignal.any([batch.signal, controller.signal]);
        patch(id, { status: "uploading", progress: null });
        try {
          await queueAttachUpload(() => uploadRemoteAttachWithProgress({
            url: task.url.trim(), kind, groupId: activeGroupId, module, signal,
            onProgress: progress => { if (!signal.aborted) patch(id, { progress }); },
            messages: { failed: t("uploadFailedRetry"), network: t("networkError") },
            ...(uploadContext?.visibilitySelectable ? { visibility: uploadVisibility } : {}),
          }), signal);
          signal.throwIfAborted();

          patch(id, { status: "success", error: "" });
          succeeded += 1;
        } catch (error) {
          if (signal.aborted) patch(id, { status: "idle", progress: null, error: "" });
          else {
            const message = error instanceof Error ? error.message : t("uploadFailedRetry");
            patch(id, { status: "error", error: message });
            failed += 1;
            if (error instanceof ApiError && [401, 403].includes(error.status)) {
              toast.error(message);
              batch.abort();
            }
          }
        } finally {
          remoteCurrentRef.current = null;
          setRemoteQueueProgress({ completed: index + 1, total: ids.length });
        }
      }
      if (succeeded) toast.success(t("uploadedCount", { count: succeeded }));
      if (failed) toast.error(t("remoteFailedCount", { count: failed }));
      if (duplicates) toast.success(t("remoteDuplicatesSkipped", { count: duplicates }));
    } finally {
      updateRemoteTasks(tasks => tasks.map(task => task.status === "queued" ? { ...task, status: "idle" } : task));

      try { if (remoteOpenRef.current) await refreshAll(); }
      finally {
        remoteCurrentRef.current = null;
        remoteAbortRef.current = null;
        setRemoteLoading(false);
      }
    }
  }

  const handleDeleteFile = useCallback((id: string) => {
    setFileDeleteTarget({ mode: "single", id });
  }, []);

  async function confirmDeleteFile() {
    if (!fileDeleteTarget) {
      return;
    }

    setFileDeleteSaving(true);

    try {
      if (fileDeleteTarget.mode === "single") {
        const result = await api<{ deleted: boolean }>(
          `/api/attach/${fileDeleteTarget.id}`,
          { method: "DELETE" },
        );

        if (!result.ok) {
          toast.error(result.message);
          return;
        }

        const deletedId = fileDeleteTarget.id;
        selectedFilesRef.current.delete(deletedId);
        setSelectedIds((current) => current.filter((id) => id !== deletedId));
        toast.success(t("deleted"));
      } else {
        if (selectedIds.length === 0) {
          return;
        }

        const result = await api<{ deleted: number }>("/api/attach/batch", {
          method: "POST",
          body: {
            action: "delete",
            ids: selectedIds,
          },
        });

        if (!result.ok) {
          toast.error(result.message);
          return;
        }

        toast.success(t("deletedSelected"));
        setSelectedIds([]);
        setSelectionVisible(false);
      }

      setFileDeleteTarget(null);
      await refreshAll();
    } finally {
      setFileDeleteSaving(false);
    }
  }

  const handleRenameFile = useCallback(async (id: string, originalName: string) => {
    const result = await api<{ attach: AttachFileRecord }>(
      `/api/attach/${id}`,
      {
        method: "PATCH",
        body: { originalName },
      },
    );

    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    setItems((current) =>
      current.map((item) =>
        item.id === id
          ? { ...item, originalName: result.data.attach.originalName }
          : item,
      ),
    );
    const selected = selectedFilesRef.current.get(id);
    if (selected) selectedFilesRef.current.set(id, { ...selected, originalName: result.data.attach.originalName });
    toast.success(t("renamed"));
  }, [api, t]);

  function handleBatchDelete() {
    if (selectedIds.length === 0) {
      return;
    }

    setFileDeleteTarget({ mode: "batch" });
  }

  function openMove(file: AttachFileRecord | null = null) {
    setMoveFile(file);
    setMoveTargetGroupId("");
    setMoveOpen(true);
  }

  async function handleReplacedFile(file: AttachFileRecord) {
    const current = selectedFilesRef.current.get(file.id);
    if (current) selectedFilesRef.current.set(file.id, { ...current, ...file });
    setItems((items) => items.map((item) => item.id === file.id ? { ...item, ...file } : item));
    await refreshAll();
  }

  async function handleBatchMove() {
    const ids = moveFile ? [moveFile.id] : selectedIds;
    if (moveSaving || !moveTargetGroupId || ids.length === 0) return;
    setMoveSaving(true);
    try {
      const result = await api<{ moved: number }>("/api/attach/batch", {
        method: "POST",
        body: { action: "move", ids, targetGroupId: moveTargetGroupId },
      });
      if (!result.ok) { toast.error(result.message); return; }
      toast.success(t("moved"));

      setSelectedIds((current) => current.filter((id) => !ids.includes(id)));
      for (const id of ids) selectedFilesRef.current.delete(id);
      if (pickedId && ids.includes(pickedId)) setPickedId(null);
      if (!moveFile) setSelectionVisible(false);
      setMoveOpen(false);
      setMoveFile(null);
      setMoveTargetGroupId("");
      await refreshAll();
    } finally { setMoveSaving(false); }
  }

  async function handleCreateGroup(input: AttachGroupInput) {
    const result = await api<{ group: AttachGroupRecord }>(
      "/api/attach/groups",
      {
        method: "POST",
        body: input,
      },
    );

    if (!result.ok) {
      toast.error(result.message);
      return false;
    }

    await loadGroups();
    handleSelectGroup(result.data.group.id);
    return true;
  }

  async function handleUpdateGroup(groupId: string, input: AttachGroupInput) {
    const result = await api<{ group: AttachGroupRecord }>(
      "/api/attach/groups",
      {
        method: "PATCH",
        body: { id: groupId, ...input },
      },
    );

    if (!result.ok) {
      toast.error(result.message);
      return false;
    }

    await loadGroups();
    return true;
  }

  async function handleDeleteGroup(groupId: string) {
    if (groups.some(group => group.parentId === groupId)) { toast.error(t("groupHasChildren")); return; }
    setDeleteGroupTarget(groupId);
  }

  async function confirmDeleteGroup() {
    if (!deleteGroupTarget) {
      return;
    }

    const result = await api<{ groups: AttachGroupRecord[] }>(
      `/api/attach/groups?id=${encodeURIComponent(deleteGroupTarget)}`,
      { method: "DELETE" },
    );

    if (!result.ok) {
      toast.error(result.message);
      return;
    }

    toast.success(t("groupDeleted"));
    setDeleteGroupTarget(null);
    setGroups(result.data.groups);

    if (activeGroupId === deleteGroupTarget) {
      const parentId = groups.find(group => group.id === deleteGroupTarget)?.parentId;
      const target = result.data.groups.find(group => group.id === parentId)
        ?? result.data.groups.find(group => group.isDefault)
        ?? result.data.groups[0];
      handleSelectGroup(target?.id ?? "default");
    } else {
      await loadList();
    }
  }

  function handleToggleSelectionMode() {
    if (isMulti) {

      if (allSelected || selectedIds.length >= effectiveMaxCount) {
        const currentPage = new Set(items.map((item) => item.id));
        setSelectedIds((current) => current.filter((id) => !currentPage.has(id)));
        return;
      }
      const currentPage = new Set(items.map((item) => item.id));
      const previous = selectedIds.filter((id) => !currentPage.has(id));
      const compatible = items.filter((file) => canSelectAttach(file, effectiveExtensions, allowedKinds).ok);
      const capped = [...previous, ...compatible.map((item) => item.id)].slice(0, effectiveMaxCount);
      if (previous.length + compatible.length > effectiveMaxCount) {
        toast.error(t("maxSelect", { count: effectiveMaxCount }));
      }
      setSelectedIds(capped);
      return;
    }

    if (!selectionVisible) {
      setSelectionVisible(true);
      setSelectedIds(items.map((item) => item.id));
      return;
    }

    if (allSelected) {
      setSelectionVisible(false);
      setSelectedIds([]);
      return;
    }

    setSelectedIds(items.map((item) => item.id));
  }

  const allSelected =
    items.length > 0 && items.every((item) => selectedIds.includes(item.id));

  const remoteCanUpload = useMemo(() => {
    const complete = new Set(remoteTasks.filter(task => task.status === "success").map(task => remoteAttachUrlKey(task.url)));
    return remoteTasks.some(task => task.url.trim() && task.status !== "success" && (task.status !== "duplicate" || !complete.has(remoteAttachUrlKey(task.url))));
  }, [remoteTasks]);

  function handleSelectGroup(groupId: string) {
    setActiveGroupId(groupId);
    setPage(1);
    setSelectedIds([]);
    setSelectionVisible(isMulti);
  }

  function handleTypeFilterChange(value: string | null) {
    if (!value) {
      return;
    }

    setTypeFilter(value === "all" ? "all" : (value as UploadKind));
    setPage(1);
    setSelectedIds([]);
    setSelectionVisible(isMulti);
  }

  return {
    fileInputRef,
    pickerTexts,
    attachTabItems,
    tab,
    setTab,
    typeFilter,
    handleTypeFilterChange,
    groups,
    activeGroupId,
    activeGroupWritable,
    readOnlyGroupHint,
    items,
    selectedIds,
    pickedId,
    page,
    pageSize,
    setPage,
    setPageSize,
    total,
    totalPages,
    quotaText,
    quotaNotice,
    remoteTasks,
    handleRemoteRowsChange,
    handleRemotePaste,
    remoteCanUpload,
    remoteQueueProgress,
    rules,
    uploadContext,
    uploadVisibility,
    setUploadVisibility,
    initialLoading,
    listFetching,
    uploadTasks,
    remoteLoading,
    handleCancelRemote: () => remoteAbortRef.current?.abort(),
    selectionVisible,
    viewMode,
    setViewMode,
    moveOpen,
    setMoveOpen,
    moveFile,
    moveSaving,
    openMove,
    handleReplacedFile,
    moveTargetGroupId,
    setMoveTargetGroupId,
    deleteGroupTarget,
    setDeleteGroupTarget,
    fileDeleteTarget,
    setFileDeleteTarget,
    fileDeleteSaving,
    effectiveExtensions,
    uploading,
    allSelected,
    isMulti,
    effectiveMaxCount,
    canConfirm: isMulti ? selectedIds.length > 0 : Boolean(pickedId),
    handleToggleSelect,
    handlePick,
    handleConfirm,
    handleUploadFiles,
    handleRemoteExtract,
    handleDeleteFile,
    confirmDeleteFile,
    handleRenameFile,
    handleCancelUpload,
    handleBatchDelete,
    handleBatchMove,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
    confirmDeleteGroup,
    handleToggleSelectionMode,
    handleSelectGroup,
  };
}
